import { useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useErrorBoundary } from 'react-error-boundary';

import { sendFEEvent } from '../../../../../eventUtil';
import { useAPI } from '../../../../../services/api.service';
import { useCheckout } from '../../../../../hooks/use-checkout';
import { SavedCardFormProps } from '../../SavedCards/SavedCards';
import { useOrderPolling } from '../../../hooks/use-order-polling';
import { usePaymentState } from '../../../hooks/use-payment-state';
import { createBraintreeSavedCardFinalizeRequestBody } from '../utils';
import {
  ESdkEvents,
  EventParams,
  BraintreeFinalizePaymentResponse,
  AppchargeErrorType,
} from '../../../../../contracts';
import { generateAppchargeErrorMessage } from '../../../../../utils/generate-appcharge-error-message';

export function useBraintreeSavedCard() {
  const { checkoutData, selectedPaymentMethod, getEventParams } = useCheckout();
  const setIsPaymentLoading = usePaymentState((state) => state.setIsPaymentLoading);
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const { API } = useAPI();
  const { startOrderPolling } = useOrderPolling();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { showBoundary } = useErrorBoundary();

  const isBraintreePaymentInProgress = useRef(false);

  const handleSubmit = async (formValues: SavedCardFormProps) => {
    if (isBraintreePaymentInProgress.current) {
      return;
    }
    isBraintreePaymentInProgress.current = true;
    setIsPaymentLoading(true);
    const FEeventParams: Partial<EventParams> = {
      isSavedPaymentMethodUsed: true,
    };
    try {
      const finalizePaymentRequestBody = createBraintreeSavedCardFinalizeRequestBody(
        formValues,
        selectedPaymentMethod!
      );
      const finalizePaymentResponse = await API.finalizePayment<BraintreeFinalizePaymentResponse>(
        finalizePaymentRequestBody
      );
      handleServerResponse(finalizePaymentResponse, FEeventParams);
    } catch (error: any) {
      const message = error?.response?.message;
      showBoundary(t('somethingWentWrong'));
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        reason: message,
        ...FEeventParams,
        error: {
          isFatal: true,
          reason: message,
        },
      });
    }
  };

  const handleServerResponse = (
    finalizePaymentResponse: BraintreeFinalizePaymentResponse,
    FEeventParams: Partial<EventParams>
  ) => {
    const { status, message } = finalizePaymentResponse.braintree;
    if (status === 'succeeded') {
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
        ...getEventParams(),
        ...FEeventParams,
      });
      startOrderPolling(checkoutData?.orderId!, FEeventParams);
    } else if (status === 'failed') {
      handleError(finalizePaymentResponse.appChargeErrorType);
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        reason: message,
        ...FEeventParams,
      });
    }
  };

  const handleError = (appChargeErrorType?: AppchargeErrorType) => {
    const errorMessage = generateAppchargeErrorMessage(t, appChargeErrorType);
    isBraintreePaymentInProgress.current = false;
    setErrorMessage(errorMessage);
    setIsPaymentLoading(false);
  };

  return { handleSubmit };
}
